import { request, Logger, StorageService } from '@nextgen-web-framework/core';
import { ApiProjections } from '../../types/enums';
import {
  CreateMyShopPayloadType,
  MyShopImageResponse,
  MyShopResponse,
  MyShops,
  CustomRequestOptions,
  MyShopRequest,
} from '../../types/myshopTypes';
import { getMyShopReqObj, VersionId, createShopReqObj, updateShopReqObj, ShopId } from '../config';
import { MyShop } from '../models';
import { uploadShopImageReqObj } from '../config/myshopApiEndPoints';
import logObject from '../../../logObject';
import { BannerInfo } from '../models/BannerInfo';
import { ProfilePicture } from '../models/ProfilePicture';

export interface IMyShopAPI {
  projections?: string;
  shopCodeUrl?: string;
}
export const myshopApi = {
  async getMyShop(
    { projections = ApiProjections.ALL, shopCodeUrl = undefined }: IMyShopAPI,
    options?: CustomRequestOptions,
  ): Promise<MyShopResponse> {
    const loggerInstance = Logger.getInstance(logObject('myshopApi.getMyShop'));
    try {
      const requestObj = { ...getMyShopReqObj };
      requestObj.url = requestObj.url.replace(VersionId, 'v1');

      requestObj.params = {
        projections,
      };

      if (shopCodeUrl && shopCodeUrl !== '') {
        requestObj.params = { shopCode: shopCodeUrl, ...requestObj.params };
      }

      if (options?.headers) {
        requestObj.headers = options?.headers;
      }
      if (options?.baseURL) {
        requestObj.url = `${options.baseURL}${requestObj.url}`;
      }
      const resp = await request<MyShop | MyShops>(requestObj);

      if (Object.keys(resp).includes('myshops')) {
        const respList = resp as MyShops;
        const { myshops } = respList;
        if (Array.isArray(myshops) && myshops.length > 0) {
          return myshops[0] as MyShop;
        }
      }
      return resp;
    } catch (error) {
      loggerInstance.error('myshopApi.getMyShop', error);
      throw error;
    }
  },
  async createMyShop({ payload }: CreateMyShopPayloadType, options?: CustomRequestOptions): Promise<MyShopResponse> {
    const loggerInstance = Logger.getInstance(logObject('myshopApi.createMyShop'));
    try {
      const requestObj = { ...createShopReqObj };
      requestObj.url = requestObj.url.replace(VersionId, 'v1');
      const payloadBody = await processPayloadBody(payload as MyShopRequest);
      requestObj.body = payloadBody;
      if (options?.headers) {
        requestObj.headers = options?.headers;
      }
      if (options?.baseURL) {
        requestObj.url = `${options.baseURL}${requestObj.url}`;
      }
      return await request<MyShopResponse>(requestObj);
    } catch (error) {
      loggerInstance.error('myshopApi.createMyShop', error);
      throw error;
    }
  },
  async updateMyShop({ payload }: CreateMyShopPayloadType, options?: CustomRequestOptions): Promise<MyShopResponse> {
    const loggerInstance = Logger.getInstance(logObject('myshopApi.updateMyShop'));
    try {
      const requestObj = { ...updateShopReqObj };
      const shopId = StorageService.getLocalStorage().retrieve('ShopId');
      const payloadBody = await processPayloadBody(payload as MyShopRequest, shopId);
      requestObj.url = requestObj.url.replace(VersionId, 'v1');
      requestObj.url = requestObj.url.replace(ShopId, shopId);
      requestObj.body = payloadBody;
      if (options?.headers) {
        requestObj.headers = options?.headers;
      }
      if (options?.baseURL) {
        requestObj.url = `${options.baseURL}${requestObj.url}`;
      }
      return await request<MyShopResponse>(requestObj);
    } catch (error) {
      loggerInstance.error('myshopApi.updateMyShop', error);
      throw error;
    }
  },

  async uploadMyShopImage(
    { payload }: CreateMyShopPayloadType,
    shopId?: string,
    options?: CustomRequestOptions,
  ): Promise<MyShopImageResponse> {
    const loggerInstance = Logger.getInstance(logObject('myshopApi.uploadMyShopImage'));
    try {
      const requestObj = { ...uploadShopImageReqObj };
      requestObj.url = requestObj.url.replace(VersionId, 'v1');
      requestObj.body = payload as unknown as BodyInit;
      if (options?.headers) {
        requestObj.headers = options?.headers;
      }
      if (options?.baseURL) {
        requestObj.url = `${options.baseURL}${requestObj.url}`;
      }
      if (shopId && shopId !== '') {
        requestObj.params = { inputShopId: shopId };
      }
      return await request<MyShopImageResponse>(requestObj, {
        skipDefaultContentType: true,
      });
    } catch (error) {
      loggerInstance.error('myshopApi.uploadMyShopImage', error);
      throw error;
    }
  },
};

export async function processPayloadBody(payload: MyShopRequest, shopId?: string): Promise<BodyInit> {
  const loggerInstance = Logger.getInstance(logObject('myshopApi.processPayloadBody'));
  try {
    const payloadBody = payload;
    const profile = getFormImage(payloadBody, 'profileImageData', 'profile');
    const banner = getFormImage(payloadBody, 'bannerImageData', 'banner');
    const uploadPromises: Promise<MyShopImageResponse>[] = [];

    addUploadPromise(uploadPromises, profile, shopId);
    addUploadPromise(uploadPromises, banner, shopId);

    if (uploadPromises.length > 0) {
      await processUploadPromises(uploadPromises, payloadBody);
    }
    return payloadBody as BodyInit;
  } catch (error) {
    loggerInstance.error('myshopApi.processPayloadBody', error);
    throw error;
  }
}

export function addUploadPromise(uploadPromises: Promise<MyShopImageResponse>[], image: FormData, shopId?: string) {
  if (image && image.get('imageData')) {
    uploadPromises.push(myshopApi.uploadMyShopImage({ payload: image }, shopId));
  }
}

export async function processUploadPromises(
  uploadPromises: Promise<MyShopImageResponse>[],
  payloadBody: MyShopRequest,
) {
  const loggerInstance = Logger.getInstance(logObject('myshopApi.processUploadPromises'));
  try {
    const results = await Promise.allSettled(uploadPromises);
    if (results.some((result) => result.status === 'rejected')) {
      throw new Error('error on upload image');
    }
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        const response = result.value;
        processResponse(response, payloadBody);
      }
    });
  } catch (error) {
    loggerInstance.error('myshopApi.processUploadPromises', error);
    throw error;
  }
}

function processResponse(response: MyShopImageResponse, payloadBody: MyShopRequest) {
  if (Object.keys(response).includes('bannerId')) {
    const resData = response as BannerInfo;
    if (resData.bannerId?.includes('banner')) {
      payloadBody.bannerImageData = undefined;
      payloadBody.bannerInfo = response as BannerInfo;
    }
    if (resData.bannerId?.includes('profile')) {
      payloadBody.profileImageData = undefined;
      payloadBody.profilePicture = {
        imageId: resData.bannerId,
        imageUrl: resData.bannerUrl,
        mime: resData.mime,
      };
    }
  }
  if (Object.keys(response).includes('imageId')) {
    payloadBody.profileImageData = undefined;
    payloadBody.profilePicture = response as ProfilePicture;
  }
}

export function getFormImage(payload: MyShopRequest, filterKey: keyof MyShopRequest, imageType: string): FormData {
  let result: FormData = new FormData();
  if (['bannerImageData', 'profileImageData'].includes(filterKey) && payload[filterKey]) {
    const data = new FormData();
    const buffer = payload[filterKey] as Buffer;
    const blob = new Blob([buffer], { type: 'image/jpeg' });
    data.append('imageData', blob, filterKey === 'bannerImageData' ? 'banner.jpg' : 'profile.jpg');
    data.append('imageType', imageType);
    result = data;
    return result;
  }

  return result;
}
export default myshopApi;
